/** The custom properties this module owns. Nothing else may write them. */
export const TOPBAR_CSS_VARS = [
    '--bj-topbar-bg',
    '--bj-topbar-border',
    '--bj-topbar-radius',
    '--bj-topbar-shadow',
];
/**
 * `#0d0028` + `0.97` → `rgba(13, 0, 40, 0.97)`.
 *
 * Colour and opacity are separate schema fields (so the Studio can drive them
 * with a picker and a slider) and are recombined only here. Alpha is emitted
 * verbatim — `0.1`, not `0.100` — because these strings are asserted literally.
 */
function rgba(hex, alpha) {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/**
 * Compose every `--bj-topbar-*` value for `config`. Pure; total; the single
 * definition of what a Top Bar config MEANS.
 *
 * `mode: 'transparent'` zeroes the three painted properties and leaves the
 * radius alone — the radius is geometry-adjacent and switching modes must not
 * move anything (`CLAUDE.md` §3 decision 4).
 */
export function topBarCssVars(config) {
    const transparent = config.mode === 'transparent';
    const { background, border, shadow } = config;
    const bg = transparent
        ? 'none'
        : background.mode === 'gradient'
            ? `linear-gradient(${background.gradientAngle}deg, ` +
                `${rgba(background.gradientFrom, background.gradientFromOpacity)} 0%, ` +
                `${rgba(background.gradientTo, background.gradientToOpacity)} 100%)`
            : rgba(background.colour, background.opacity);
    return {
        '--bj-topbar-bg': bg,
        '--bj-topbar-border': transparent || !border.enabled
            ? 'none'
            : `${border.thickness}px solid ${rgba(border.colour, border.opacity)}`,
        '--bj-topbar-radius': `${border.radius}px`,
        '--bj-topbar-shadow': transparent || !shadow.enabled
            ? 'none'
            : `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ` +
                rgba(shadow.colour, shadow.opacity),
    };
}
/**
 * Write every `--bj-topbar-*` property onto `element`.
 *
 * The element is a parameter rather than assumed to be `documentElement`: the
 * app writes the document root at boot, while the Studio writes a scoped preview
 * container so tuning cannot restyle the Studio's own chrome (WP-54.5).
 */
export function applyTopBarVars(config, element) {
    const vars = topBarCssVars(config);
    for (const name of TOPBAR_CSS_VARS) {
        element.style.setProperty(name, vars[name]);
    }
}
